import axios from "axios";
import { API_URLS } from "../config/api";
import { authAPI } from "./AuthService";

// Create axios instance with default configuration
const apiClient = axios.create({
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor for logging and adding auth headers if needed
apiClient.interceptors.request.use(
  (config) => {
    console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
    return config;
  },
  (error) => {
    console.error("API Request Error:", error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => {
    console.log(`API Response: ${response.status} ${response.config.url}`);
    return response;
  },
  (error) => {
    console.error("API Response Error:", error.response?.data || error.message);
    return Promise.reject(error);
  }
);

/**
 * Crop API methods
 */
export const cropAPI = {
  // Get all crops
  getAllCrops: async () => {
    try {
      const response = await apiClient.get(API_URLS.CROPS);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch crops: ${error.message}`);
    }
  },

  // Create a new crop
  createCrop: async (cropData) => {
    try {
      const response = await apiClient.post(API_URLS.CROPS, cropData);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to create crop: ${error.message}`);
    }
  },

  // Get crops by client
  getCropsByClient: async (clientId) => {
    try {
      const response = await apiClient.get(API_URLS.CLIENT_CROPS(clientId));
      return response.data;
    } catch (error) {
      throw new Error(
        `Failed to fetch crops for client ${clientId}: ${error.message}`
      );
    }
  },
};

/**
 * Tree API methods
 */
export const treeAPI = {
  // Get all trees
  getAllTrees: async () => {
    try {
      const response = await apiClient.get(API_URLS.TREES);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch trees: ${error.message}`);
    }
  },

  // Create a new tree
  createTree: async (treeData) => {
    try {
      const response = await apiClient.post(API_URLS.TREES, treeData);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to create tree: ${error.message}`);
    }
  },

  // Get tree by ID
  getTreeById: async (id) => {
    try {
      const response = await apiClient.get(API_URLS.TREE_BY_ID(id));
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch tree with ID ${id}: ${error.message}`);
    }
  },

  // Get trees by client
  getTreesByClient: async (clientId) => {
    try {
      const response = await apiClient.get(API_URLS.CLIENT_TREES(clientId));
      return response.data;
    } catch (error) {
      throw new Error(
        `Failed to fetch trees for client ${clientId}: ${error.message}`
      );
    }
  },
};

/**
 * Client API methods
 */
export const clientAPI = {
  // Get all clients
  getAllClients: async () => {
    try {
      const response = await apiClient.get(API_URLS.CLIENTS);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch clients: ${error.message}`);
    }
  },

  // Create a new client
  createClient: async (clientData) => {
    try {
      const response = await apiClient.post(API_URLS.CLIENTS, clientData);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to create client: ${error.message}`);
    }
  },

  // Get client by ID
  getClientById: async (id) => {
    try {
      const response = await apiClient.get(API_URLS.CLIENT_BY_ID(id));
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch client with ID ${id}: ${error.message}`);
    }
  },

  // Update a client
  updateClient: async (id, clientData) => {
    try {
      const response = await apiClient.put(
        API_URLS.CLIENT_BY_ID(id),
        clientData
      );
      return response.data;
    } catch (error) {
      throw new Error(
        `Failed to update client with ID ${id}: ${error.message}`
      );
    }
  },

  // Delete a client
  deleteClient: async (id) => {
    try {
      const response = await apiClient.delete(API_URLS.CLIENT_BY_ID(id));
      return response.data;
    } catch (error) {
      throw new Error(
        `Failed to delete client with ID ${id}: ${error.message}`
      );
    }
  },
};

/**
 * General API health check
 */
export const healthCheck = async () => {
  try {
    const response = await apiClient.get(API_URLS.HEALTH);
    return { online: true, message: "API is reachable", data: response.data };
  } catch (error) {
    return { online: false, message: error.message };
  }
};

export default {
  auth: authAPI,
  crops: cropAPI,
  trees: treeAPI,
  clients: clientAPI,
  healthCheck,
};
